import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, Music, Mic, Speaker, Piano, Guitar, Drum, Search, Filter, MapPin, Star, ChevronDown, ChevronUp, Moon, Sun } from 'lucide-react';
import Header from './Header';

const RoomList = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState('');
  const [duration, setDuration] = useState(2);
  const [filterSize, setFilterSize] = useState('all');
  const [budgetRange, setBudgetRange] = useState('all');
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Sample room data
  const rooms = [
    {
      id: 1,
      name: "Studio A - The Cathedral",
      size: "band",
      capacity: "5-8 people",
      price: 45,
      rating: 4.8,
      image: "https://mockimg.co/400x250",
      facilities: ["piano", "drums", "amps", "recording", "ac"],
      available: ["9:00", "13:00", "18:00", "21:00"],
      description: "Professional recording studio with high-end equipment"
    },
    {
      id: 2,
      name: "Practice Room B",
      size: "solo",
      capacity: "1-2 people",
      price: 25,
      rating: 4.6,
      image: "https://mockimg.co/400x250",
      facilities: ["piano", "ac"],
      available: ["9:00", "13:00", "18:00", "21:00"],
      description: "Intimate space perfect for solo practice and small sessions"
    },
    {
      id: 3,
      name: "Ensemble Hall C",
      size: "ensemble",
      capacity: "8-15 people",
      price: 65,
      rating: 4.9,
      image: "https://mockimg.co/400x250",
      facilities: ["piano", "drums", "amps", "mics", "pa", "recording"],
      available: ["9:00", "13:00", "18:00", "21:00"],
      description: "Spacious hall ideal for larger groups and full band rehearsals"
    },
    {
      id: 4,
      name: "Studio D - Acoustic Haven",
      size: "band",
      capacity: "3-6 people",
      price: 40,
      rating: 4.7,
      image: "https://mockimg.co/400x250",
      facilities: ["piano", "amps", "mics", "ac"],
      available: ["9:00", "13:00", "18:00", "21:00"],
      description: "Acoustically treated room with vintage gear"
    },
    {
      id: 5,
      name: "Solo Pod E",
      size: "solo",
      capacity: "1 person",
      price: 20,
      rating: 4.5,
      image: "https://mockimg.co/400x250",
      facilities: ["piano", "ac"],
      available: ["9:00", "13:00", "18:00", "21:00"],
      description: "Cozy individual practice space"
    },
    {
      id: 6,
      name: "Live Room F",
      size: "ensemble",
      capacity: "10-20 people",
      price: 80,
      rating: 4.8,
      image: "https://mockimg.co/400x250",
      facilities: ["piano", "drums", "amps", "mics", "pa", "recording", "stage"],
      available: ["9:00", "13:00", "18:00", "21:00"],
      description: "Concert-style venue with professional stage setup"
    }
  ];

  const facilityIcons = {
    piano: <Piano size={16} />,
    drums: <Drum size={16} />,
    amps: <Speaker size={16} />,
    mics: <Mic size={16} />,
    recording: <Music size={16} />,
    pa: <Speaker size={16} />,
    ac: "❄️",
    stage: "🎭"
  };

  const facilityNames = {
    piano: "Piano",
    drums: "Drum Kit",
    amps: "Guitar Amps",
    mics: "Microphones",
    recording: "Recording",
    pa: "PA System",
    ac: "Air Con",
    stage: "Stage"
  };

  const sizeLabels = {
    solo: "Solo",
    band: "Band",
    ensemble: "Ensemble"
  };

  const getSizeColor = (size) => {
    switch(size) {
      case 'solo': return 'bg-blue-100 text-blue-800';
      case 'band': return 'bg-green-100 text-green-800';
      case 'ensemble': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredRooms = rooms.filter(room => {
    const matchesSize = filterSize === 'all' || room.size === filterSize;
    const matchesBudget = budgetRange === 'all' ||
      (budgetRange === 'budget' && room.price <= 30) ||
      (budgetRange === 'mid' && room.price > 30 && room.price <= 50) ||
      (budgetRange === 'premium' && room.price > 50);
    return matchesSize && matchesBudget;
  });

  const timeSlots = [
    { time: "9:00", label: "Morning" },
    { time: "13:00", label: "Afternoon" },
    { time: "18:00", label: "Evening" },
    { time: "21:00", label: "Late Evening" }
  ];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const handleRoomSelect = (room) => {
    navigate(`/room/${room.id}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Upsell Banner - Only show for solo rooms */}
        {filterSize === 'solo' && (
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl shadow-lg p-6 mb-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold mb-2">Need More Space?</h3>
                <p className="text-indigo-100">Upgrade to our larger ensemble rooms for just $40 extra and get premium equipment included!</p>
              </div>
              <button className="bg-white text-indigo-600 px-6 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors whitespace-nowrap">
                Upgrade Now
              </button>
            </div>
          </div>
        )}

        <div className="flex gap-8">
          {/* Sidebar with Filters */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border dark:border-gray-700 sticky top-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                <Filter className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
                Filter Rooms
              </h2>

              {/* Date Picker */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{formatDate(selectedDate)}</p>
              </div>

              {/* Time Picker */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Time</label>
                <select
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Any time</option>
                  {timeSlots.map(({ time, label }) => (
                    <option key={time} value={time}>{label} ({time})</option>
                  ))}
                </select>
              </div>

              {/* Duration */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Duration</label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value={1}>1 hour</option>
                  <option value={2}>2 hours</option>
                  <option value={3}>3 hours</option>
                  <option value={4}>4 hours</option>
                  <option value={6}>6 hours</option>
                  <option value={8}>8 hours</option>
                </select>
              </div>

              {/* Budget Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Budget Range</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="budget"
                      value="all"
                      checked={budgetRange === 'all'}
                      onChange={(e) => setBudgetRange(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Any Budget</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="budget"
                      value="budget"
                      checked={budgetRange === 'budget'}
                      onChange={(e) => setBudgetRange(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Budget ($20-30/hr)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="budget"
                      value="mid"
                      checked={budgetRange === 'mid'}
                      onChange={(e) => setBudgetRange(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Mid-range ($30-50/hr)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="budget"
                      value="premium"
                      checked={budgetRange === 'premium'}
                      onChange={(e) => setBudgetRange(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Premium ($50+/hr)</span>
                  </label>
                </div>
              </div>

              {/* Room Type Filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Room Type</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="roomType"
                      value="all"
                      checked={filterSize === 'all'}
                      onChange={(e) => setFilterSize(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">All Types</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="roomType"
                      value="solo"
                      checked={filterSize === 'solo'}
                      onChange={(e) => setFilterSize(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Solo Practice</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="roomType"
                      value="band"
                      checked={filterSize === 'band'}
                      onChange={(e) => setFilterSize(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Band (3-8 people)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="roomType"
                      value="ensemble"
                      checked={filterSize === 'ensemble'}
                      onChange={(e) => setFilterSize(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Ensemble (8+ people)</span>
                  </label>
                </div>
              </div>

              {/* Clear Filters */}
              <button
                onClick={() => {
                  setFilterSize('all');
                  setBudgetRange('all');
                  setSelectedTime('');
                }}
                className="w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Available Rooms {formatDate(selectedDate) === 'Today' ? 'Today' : `for ${formatDate(selectedDate)}`}
              </h2>
              <p className="text-gray-600 dark:text-gray-300">{filteredRooms.length} rooms available</p>
            </div>

            {/* Room Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredRooms.map(room => (
                <div
                  key={room.id}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border dark:border-gray-700 cursor-pointer"
                  onClick={() => handleRoomSelect(room)}
                >
                  {/* Room Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={room.image}
                      alt={room.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSizeColor(room.size)}`}>
                        {sizeLabels[room.size]}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-2 py-1 rounded-lg flex items-center">
                      <Star className="h-4 w-4 text-yellow-400 mr-1" />
                      <span className="text-sm font-medium dark:text-white">{room.rating}</span>
                    </div>
                  </div>

                  {/* Room Details */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{room.name}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center mt-1">
                          <Users className="h-4 w-4 mr-1" />
                          {room.capacity}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">${room.price}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">per hour</p>
                      </div>
                    </div>

                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{room.description}</p>

                    {/* Facilities */}
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Facilities:</p>
                      <div className="flex flex-wrap gap-2">
                        {room.facilities.map(facility => (
                          <span
                            key={facility}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                          >
                            <span className="mr-1">{facilityIcons[facility]}</span>
                            {facilityNames[facility]}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Available Times */}
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        Available Times:
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {room.available.slice(0, 6).map(time => (
                          <button
                            key={time}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTime(time);
                            }}
                            className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                              selectedTime === time
                                ? 'bg-indigo-600 text-white border-indigo-600'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30'
                            }`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                      {room.available.length > 6 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          +{room.available.length - 6} more times available
                        </p>
                      )}
                    </div>

                    {/* Book Button */}
                    <button
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRoomSelect(room);
                      }}
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Book Now - ${room.price * duration} total
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {filteredRooms.length === 0 && (
              <div className="text-center py-12">
                <Music className="h-12 w-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No rooms found</h3>
                <p className="text-gray-600 dark:text-gray-400">Try adjusting your search criteria or selecting a different date.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomList;